import { useCallback, useEffect, useState } from "react";

type PointerState = "down" | "up" | undefined;

let currentPointerState: PointerState = undefined;
const subscribers = new Set<(s: PointerState) => void>();

function notify(newState: PointerState) {
  currentPointerState = newState;
  subscribers.forEach((callback) => callback(newState));
}

function onDocumentPointerDown(e: PointerEvent) {
  if (e.button === 0) {
    notify("down");
  }
}

function onDocumentPointerUp(e: PointerEvent) {
  if (e.button === 0) {
    notify("up");
  }
}

function startListening(callback: (s: PointerState) => void) {
  if (subscribers.size === 0) {
    document.addEventListener("pointerdown", onDocumentPointerDown);
    document.addEventListener("pointerup", onDocumentPointerUp);
  }
  subscribers.add(callback);
}

function stopListening(callback: (s: PointerState) => void) {
  subscribers.delete(callback);
  if (subscribers.size === 0) {
    document.removeEventListener("pointerdown", onDocumentPointerDown);
    document.removeEventListener("pointerup", onDocumentPointerUp);
  }
}

/**
 * Detect if the user currently presses or releases a mouse button.
 * @returns {PointerState} The current pointer state - "down", "up", or undefined (before initialization)
 */
export function usePointerInteractions() {
  const [pointerState, setPointerState] = useState(currentPointerState);

  const callbackOnPointerStateChange = useCallback((newState: PointerState) => {
    setPointerState(newState);
  }, []);

  useEffect(() => {
    startListening(callbackOnPointerStateChange);

    return () => stopListening(callbackOnPointerStateChange);
  }, [callbackOnPointerStateChange]);

  return pointerState;
}
